import { useCallback, useMemo } from 'react';
import { FiltersParams } from 'types/params';
import { SearchFilterMeta } from 'controllers/SearchController/types';
import { useSearchController } from 'controllers/SearchController/SearchContext';
import { useFiltersContext } from 'components/Filters/context/FiltersContext/FiltersContext';
import {
    FirstLevelItemObject,
    SecondLevelItemObject,
} from 'components/Filters/context/FiltersContext/types';
import { useCurrentSearchState } from 'controllers/SearchController/hooks/useCurrentSearchState';

type DataSets = {
    filters: FirstLevelItemObject[];
    activeFilters: string[];
    samplesFilterMeta: SearchFilterMeta;
    packsFilterMeta: SearchFilterMeta;
};

type SupportedFilters = keyof Pick<
    typeof FiltersParams,
    'instruments' | 'genres' | 'sfxs'
>;

export const useFilterVisibility = () => {
    const {
        filtersData,
        allSamplesTotal = 0,
        allPacksTotal = 0,
    } = useFiltersData();

    const getFiltersToDisplay = useCallback(
        (filterType: SupportedFilters) => {
            const {
                samplesFilterMeta,
                packsFilterMeta,
                filters,
                activeFilters,
            } = filtersData.get(filterType)!;

            const checkedFilters = new Set(activeFilters);
            const filtersMeta = getFiltersWithBiggestResults({
                samplesFilterMeta,
                packsFilterMeta,
            });
            const filtersToDisplay = filters
                .filter((filter) =>
                    isFilterVisible({
                        filter,
                        checkedFilters,
                        filtersMeta,
                        samplesFilterMeta,
                        allSamplesTotal,
                        packsFilterMeta,
                        allPacksTotal,
                    }),
                )
                .map((filter) => {
                    filter.list =
                        filter.list?.filter((subFilter) =>
                            isFilterVisible({
                                filter: subFilter,
                                checkedFilters,
                                filtersMeta,
                                samplesFilterMeta,
                                allSamplesTotal,
                                packsFilterMeta,
                                allPacksTotal,
                            }),
                        ) ?? [];

                    return filter;
                });

            const filterCodes = new Set(
                filtersToDisplay.map((filter) => filter.code),
            );

            return filters.filter((filter) => filterCodes.has(filter.code));
        },
        [filtersData, allSamplesTotal, allPacksTotal],
    );

    const hasSomeResults = (allSamplesTotal || allPacksTotal) > 0;

    return {
        [FiltersParams.instruments]: getFiltersToDisplay(
            FiltersParams.instruments,
        ),
        [FiltersParams.genres]: getFiltersToDisplay(FiltersParams.genres),
        [FiltersParams.sfxs]: getFiltersToDisplay(FiltersParams.sfxs),
        isBpmAndKeyVisible: hasSomeResults,
    };
};

function useFiltersData() {
    const {
        instruments: activeInstrumentFilters,
        genres: activeGenreFilters,
        sfxs: activeSfxsFilters,
    } = useCurrentSearchState();

    const {
        allSamplesTotal,
        samplesAllFiltersMeta,
        allPacksTotal,
        packsAllFiltersMeta,
    } = useSearchController();
    const { listOfInstruments, listOfGenres, listOfSfxs } = useFiltersContext();

    const filtersData = useMemo(
        () =>
            new Map<SupportedFilters, DataSets>([
                [
                    FiltersParams.instruments,
                    {
                        filters: listOfInstruments?.list ?? [],
                        activeFilters:
                            activeInstrumentFilters !== null
                                ? activeInstrumentFilters
                                : [],
                        samplesFilterMeta:
                            samplesAllFiltersMeta.filterInstrumentsMeta ?? {},
                        packsFilterMeta:
                            packsAllFiltersMeta.filterInstrumentsMeta ?? {},
                    },
                ],
                [
                    FiltersParams.genres,
                    {
                        filters: listOfGenres?.list ?? [],
                        activeFilters:
                            activeGenreFilters !== null
                                ? activeGenreFilters
                                : [],
                        samplesFilterMeta:
                            samplesAllFiltersMeta.filterGenresMeta ?? {},
                        packsFilterMeta:
                            packsAllFiltersMeta.filterGenresMeta ?? {},
                    },
                ],
                [
                    FiltersParams.sfxs,
                    {
                        filters: listOfSfxs?.list ?? [],
                        activeFilters:
                            activeSfxsFilters !== null ? activeSfxsFilters : [],
                        samplesFilterMeta:
                            samplesAllFiltersMeta.filterSfxsMeta ?? {},
                        packsFilterMeta:
                            packsAllFiltersMeta.filterSfxsMeta ?? {},
                    },
                ],
            ]),
        [
            samplesAllFiltersMeta,
            packsAllFiltersMeta,
            activeInstrumentFilters,
            activeGenreFilters,
            activeSfxsFilters,
            listOfInstruments,
            listOfGenres,
            listOfSfxs,
        ],
    );

    return {
        filtersData,
        allSamplesTotal: allSamplesTotal ?? 0,
        allPacksTotal: allPacksTotal ?? 0,
    };
}

export function isFilterVisible({
    filter,
    checkedFilters,
    filtersMeta,
    allSamplesTotal,
    samplesFilterMeta,
    allPacksTotal,
    packsFilterMeta,
}: {
    filter: FirstLevelItemObject | SecondLevelItemObject;
    checkedFilters: Set<string>;
    filtersMeta: Map<string, number>;
    allSamplesTotal: number;
    allPacksTotal: number;
    samplesFilterMeta: SearchFilterMeta;
    packsFilterMeta: SearchFilterMeta;
}) {
    const isChecked = checkedFilters.has(filter.code);

    const filterCount = filtersMeta.get(filter.code);
    const resultCount =
        filtersMeta.has(filter.code) && filterCount ? filterCount : 0;
    const hasResults = resultCount > 0;

    const hasSameSampleCount =
        (samplesFilterMeta[filter.code] ?? 0) === allSamplesTotal;
    const hasSamePackCount =
        (packsFilterMeta[filter.code] ?? 0) === allPacksTotal;

    const hasSameNumberOfResults = hasSameSampleCount && hasSamePackCount;

    return isChecked || (hasResults && !hasSameNumberOfResults);
}

export function getFiltersWithBiggestResults({
    samplesFilterMeta,
    packsFilterMeta,
}: {
    samplesFilterMeta: SearchFilterMeta;
    packsFilterMeta: SearchFilterMeta;
}) {
    const allFiltersMeta = [samplesFilterMeta, packsFilterMeta];

    return allFiltersMeta
        .flatMap((filter) => Object.entries(filter) as [string, number][])
        .reduce<Map<string, number>>(
            (filters, [code, count = 0]: [string, number]) => {
                if (filters.has(code)) {
                    if (filters.get(code)! < count) {
                        filters.set(code, count);
                    }
                } else {
                    filters.set(code, count);
                }
                return filters;
            },
            new Map<string, number>(),
        );
}
